import { Delete, Folder } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import { paths } from "@rms-types/constants/paths";
import { useMemo } from "react";

import { ConversationListItem, getUnreadMessageCount, NewConversationButton } from "@/features/conversation";
import {
  ConversationFoldersCreateButton,
  useDeleteConversationFolderConfirmation,
} from "@/features/conversation-folder";
import { useDashboardSidebarStore } from "@/hooks";
import { ExpandingSidebar } from "@/layouts/expanding-sidebar-layout/components";
import { SidebarList } from "@/layouts/expanding-sidebar-layout/components/SidebarList";
import { SidebarItem } from "@/layouts/types";

const primarySidebarItems: SidebarItem[] = [
  {
    label: "Inbox",
    identifier: "messages-inbox",
    link: paths.app.messages.inbox.getLink(),
    permissions: paths.app.messages.inbox.permissions,
  },
  {
    label: "Favorites",
    identifier: "messages-favorites",
    link: paths.app.messages.favorites.getLink(),
    permissions: paths.app.messages.favorites.permissions,
  },
  {
    label: "Trash",
    identifier: "messages-trash",
    link: paths.app.messages.trash.getLink(),
    permissions: paths.app.messages.trash.permissions,
  },
];

export function MessageExpandingSidebarLayout() {
  const { conversationSidebar } = useDashboardSidebarStore();
  const { handleDelete, isDeleteConversationFolderPending } = useDeleteConversationFolderConfirmation();

  const folderSidebarItems: SidebarItem[] = useMemo(() => {
    return conversationSidebar.data.folders
      .sort((a, b) => a.folder.name.localeCompare(b.folder.name))
      .map(({ folder, conversations }) => ({
        identifier: `folder-${folder.id}`,
        label: (
          <ConversationListItem
            name={folder.name}
            count={getUnreadMessageCount(conversations)}
            icon={<Folder sx={{ color: (theme) => theme.palette.grey[500] }} />}
            secondaryAction={
              <IconButton
                disableRipple
                sx={{ p: 0 }}
                onClick={(event) => {
                  event.preventDefault();
                  handleDelete(folder);
                }}
                disabled={isDeleteConversationFolderPending}
              >
                <Delete color="error" fontSize="small" />
              </IconButton>
            }
          />
        ),
        link: paths.app.messages.folder.details.getLink(folder.id),
        permissions: paths.app.messages.folder.permissions,
      }));
  }, [conversationSidebar, handleDelete, isDeleteConversationFolderPending]);

  return (
    <ExpandingSidebar variant="content">
      <Box p={2}>
        <NewConversationButton />
      </Box>

      <SidebarList sidebarItems={primarySidebarItems} />

      <Box px={2}>
        <ConversationFoldersCreateButton />
      </Box>

      <SidebarList sidebarItems={folderSidebarItems} shouldRedirectOnMount={false} />
    </ExpandingSidebar>
  );
}
